import { useState, useEffect } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose } from "@/components/ui/drawer";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Trash2, CheckSquare, Square, X } from "lucide-react";
import api from "@/lib/api";

// Prepend REACT_APP_BACKEND_URL solo se l'url è relativo ("/api/..."). Gli URL esterni (http/https) vengono lasciati intatti.
export function resolveUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

// Logo dell'app: mascotte ufficiale "Oratorio Pietre Vive" / SS. Medici Bitonto
export function AppLogo({ size = 40, className = "" }) {
  return (
    <div
      className={`rounded-2xl overflow-hidden bg-white shadow-sm shrink-0 ${className}`}
      style={{ width: size, height: size }}
      aria-label="Oratorio SS. Medici"
      role="img"
    >
      <img
        src="/logo.jpg"
        alt="Oratorio SS. Medici"
        className="w-full h-full object-contain select-none"
        draggable={false}
      />
    </div>
  );
}

export function BottomSheet({ open, onOpenChange, title, description, children, testId }) {
  // Alla chiusura ripuliamo sempre eventuali blocchi lasciati da dialog annidati
  const handleOpenChange = (v) => {
    onOpenChange?.(v);
    if (!v) requestAnimationFrame(sbloccaInterfaccia);
  };
  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent data-testid={testId || "bottom-sheet"} className="rounded-t-3xl max-h-[92dvh] bg-white">
        {/* Intestazione FUORI dall'area scrollabile: è da qui che vaul fa partire
            il drag-to-dismiss (dentro un contenitore con overflow il gesto non parte). */}
        <div className="mx-auto w-full max-w-2xl shrink-0 px-5">
          <DrawerHeader className="px-0 pt-2 pb-3 flex flex-row items-start justify-between gap-3 text-left space-y-0">
            <div className="min-w-0">
              <DrawerTitle className="font-display text-xl font-extrabold text-slate-900">{title}</DrawerTitle>
              {description && <DrawerDescription className="mt-1">{description}</DrawerDescription>}
            </div>
            {/* Chiusura esplicita: non tutti scoprono il trascinamento */}
            <DrawerClose
              className="shrink-0 -mr-1 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              aria-label="Chiudi"
              data-testid="bottom-sheet-close">
              <X className="h-5 w-5" />
            </DrawerClose>
          </DrawerHeader>
        </div>
        {/* Solo il corpo scrolla; overscroll-contain evita che lo scroll "sfondi" sulla pagina */}
        <div className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto overscroll-contain px-5 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          {children}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div className="mb-4 min-w-0">
      <label className="label-app">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

/**
 * Selettore di stagione (lotto 12).
 *
 * Compare SOLO a chi può guardare indietro — oggi il solo Amministratore. Agli
 * altri non si mostra un menu con opzioni che il server rifiuterebbe: un
 * selettore che propone scelte impossibili è peggio di un selettore assente.
 *
 * Le annate arrivano da `/stagioni`, che le ricava dai dati esistenti: se
 * un'estate si salta, quell'anno non compare.
 */
export function SelettoreStagione({ valore, onChange, testId = "selettore-stagione" }) {
  const [dati, setDati] = useState(null);

  useEffect(() => {
    api.get("/stagioni").then((r) => setDati(r.data)).catch(() => setDati(null));
  }, []);

  if (!dati?.puo_vedere_storico || (dati.stagioni || []).length < 2) return null;
  const attuale = valore || dati.stagione_corrente;
  const passata = attuale !== dati.stagione_corrente;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Stagione</label>
      <select className="input-app !py-1.5 !w-auto text-sm" value={attuale}
        onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-select`}>
        {dati.stagioni.map((s) => (
          <option key={s} value={s}>{s}{s === dati.stagione_corrente ? " (in corso)" : ""}</option>
        ))}
      </select>
      {passata && (
        <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-1"
          data-testid={`${testId}-avviso`}>
          Stai guardando un'annata chiusa · la consultazione è tracciata
        </span>
      )}
    </div>
  );
}

export function Empty({ icon: Icon, text, sub }) {
  return (
    <div className="card-app p-10 text-center" data-testid="empty-state">
      {Icon && <Icon className="mx-auto h-10 w-10 text-slate-300 mb-3" strokeWidth={1.5} />}
      <p className="font-display font-bold text-slate-500">{text}</p>
      {sub && <p className="text-sm text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

// Checkbox rotondo con feedback tattile, usato nelle selezioni bulk.
export function BulkCheck({ checked, onClick, testId, size = 20 }) {
  const Icon = checked ? CheckSquare : Square;
  const cls = checked ? "text-amber-500" : "text-slate-300 hover:text-slate-400";
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick?.(); }} className={`shrink-0 transition-colors ${cls}`} data-testid={testId} aria-label="Seleziona">
      <Icon strokeWidth={2} style={{ width: size, height: size }} />
    </button>
  );
}

// Barra flottante che appare in modalità selezione: mostra il conteggio e i pulsanti azione.
export function BulkBar({ count, total, onSelectAll, onClear, actions, testId }) {
  if (count === 0 && total === 0) return null;
  return (
    <div className="sticky top-2 z-10 mb-4 rounded-3xl bg-slate-900 text-white px-4 py-3 shadow-lg flex items-center gap-3 flex-wrap" data-testid={testId || "bulk-bar"}>
      <button type="button" onClick={onClear} className="text-white/70 hover:text-white shrink-0" data-testid="bulk-close-btn" aria-label="Chiudi selezione">
        <X className="h-4 w-4" />
      </button>
      <p className="text-sm font-bold flex-1" data-testid="bulk-count">
        {count === 0 ? "Seleziona elementi" : `${count} selezionati`}
      </p>
      {onSelectAll && (
        <button type="button" onClick={onSelectAll} className="text-xs font-bold text-amber-300 hover:text-amber-200 shrink-0" data-testid="bulk-select-all-btn">
          {count === total ? "Deseleziona tutto" : `Seleziona tutti (${total})`}
        </button>
      )}
      <div className="flex gap-2 flex-wrap w-full sm:w-auto">{actions}</div>
    </div>
  );
}

/**
 * Rete di sicurezza contro l'app "congelata".
 *
 * Radix, mentre un dialog è aperto, mette `pointer-events: none` sul <body> e lo
 * rimuove alla chiusura. Se però il dialog viene SMONTATO insieme al contenitore
 * che lo ospita (es. si conferma un'eliminazione dentro un BottomSheet e
 * l'handler chiude subito lo sheet), quella pulizia non viene mai eseguita e la
 * pagina resta non cliccabile finché non si ricarica.
 */
export function sbloccaInterfaccia() {
  const body = document.body;
  const html = document.documentElement;

  // 1. Blocchi messi da Radix e da vaul su body/html
  ["pointer-events", "overflow", "position", "top", "width"].forEach((p) => {
    body.style.removeProperty(p);
    html.style.removeProperty(p);
  });
  ["data-scroll-locked", "data-vaul-drawer-locked", "aria-hidden"].forEach((a) => {
    body.removeAttribute(a);
    html.removeAttribute(a);
  });

  // 2. Overlay/portali rimasti orfani dopo uno smontaggio interrotto: sono
  //    invisibili (animazione di uscita a metà) ma continuano a mangiarsi i click.
  //
  //    NB: si NEUTRALIZZANO, non si rimuovono. Staccarli dal DOM con .remove()
  //    li sottrae a React, che al successivo aggiornamento fallisce con
  //    "NotFoundError: removeChild ... not a child of this node" (l'errore
  //    compariva chiudendo uno sheet che conteneva un dialog di conferma).
  //    L'operazione è reversibile: se l'elemento torna attivo viene ripristinato.
  const MARCA = "data-neutralizzato";

  document.querySelectorAll(`[${MARCA}]`).forEach((el) => {
    if (el.getAttribute("data-state") !== "closed" && el.hasChildNodes()) {
      el.style.removeProperty("pointer-events");
      el.style.removeProperty("display");
      el.removeAttribute(MARCA);
    }
  });

  document.querySelectorAll(
    "[data-radix-portal],[data-radix-popper-content-wrapper],[data-vaul-overlay],[data-vaul-drawer-wrapper] > [data-state='closed']"
  ).forEach((el) => {
    if (el.getAttribute("data-state") === "closed" || !el.hasChildNodes()) {
      el.style.pointerEvents = "none";
      el.style.display = "none";
      el.setAttribute(MARCA, "");
    }
  });

  // 3. Riabilita il contenuto che Radix nasconde agli screen reader
  document.querySelectorAll("[aria-hidden='true'][data-aria-hidden]").forEach((el) => {
    el.removeAttribute("aria-hidden");
    el.removeAttribute("data-aria-hidden");
  });
}

/**
 * Ultima difesa: se nonostante tutto l'interfaccia risulta bloccata, il primo
 * click dell'utente la sblocca da solo. Con `pointer-events: none` sul body i
 * click non raggiungono gli elementi, ma arrivano comunque a document in fase
 * di cattura: se non c'è nessun dialog aperto, il blocco è spurio e va rimosso.
 */
if (typeof document !== "undefined" && !window.__sbloccoInterfacciaAttivo) {
  window.__sbloccoInterfacciaAttivo = true;
  document.addEventListener(
    "pointerdown",
    () => {
      const bloccato =
        getComputedStyle(document.body).pointerEvents === "none" ||
        document.body.hasAttribute("data-scroll-locked");
      const dialogAperto = document.querySelector(
        "[role='dialog'][data-state='open'],[role='alertdialog'][data-state='open']"
      );
      if (bloccato && !dialogAperto) sbloccaInterfaccia();
    },
    true
  );
}

/**
 * Esegue la conferma NELL'ORDINE GIUSTO: prima chiude il dialog, poi (al frame
 * successivo, a pulizia avvenuta) lancia l'azione, che può chiudere lo sheet.
 */
function useConferma(onConfirm) {
  const [open, setOpen] = useState(false);

  // Se il componente sparisce mentre il dialog è aperto, sblocchiamo comunque
  useEffect(() => () => sbloccaInterfaccia(), []);

  const setOpenSicuro = (v) => {
    setOpen(v);
    if (!v) requestAnimationFrame(sbloccaInterfaccia);
  };

  const conferma = (e) => {
    e?.preventDefault();
    setOpen(false);
    requestAnimationFrame(() => {
      sbloccaInterfaccia();
      onConfirm?.();
    });
  };

  return { open, setOpenSicuro, conferma };
}

/**
 * Conferma di eliminazione IN LINEA (niente dialog modale).
 *
 * Perché non un AlertDialog: questo bottone vive sempre dentro un BottomSheet
 * (drawer vaul). Un modale Radix aperto dentro un altro modale mette
 * `pointer-events: none` sul <body>; se poi l'azione chiude lo sheet, il dialog
 * viene smontato prima della pulizia e la pagina resta non cliccabile fino al
 * refresh. Con la conferma in linea il problema non può proprio esistere.
 */
export function DeleteButton({ onConfirm, label = "Elimina", entity = "questo elemento", testId }) {
  const [confermando, setConfermando] = useState(false);

  if (!confermando) {
    return (
      <button type="button" className="btn-danger" onClick={() => setConfermando(true)} data-testid={testId || "delete-btn"}>
        <Trash2 className="h-4 w-4" /> {label}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap justify-end rounded-2xl bg-red-50 border border-red-200 px-3 py-2" data-testid="delete-confirm-inline">
      <p className="text-xs font-bold text-red-700 mr-auto">
        {"Eliminare "}{entity}{"? Non è reversibile."}
      </p>
      <button type="button" className="btn-ghost !py-1.5 text-xs" onClick={() => setConfermando(false)} data-testid="delete-cancel-btn">
        Annulla
      </button>
      <button
        type="button"
        className="btn-danger !py-1.5 text-xs"
        onClick={() => { setConfermando(false); onConfirm?.(); }}
        data-testid="delete-confirm-btn"
      >
        <Trash2 className="h-3.5 w-3.5" /> Sì, elimina
      </button>
    </div>
  );
}

// Come DeleteButton, ma renderizza direttamente uno slot custom (es. bottone dentro la BulkBar).
export function ConfirmAction({ children, onConfirm, title = "Confermi l'operazione?", description, testId }) {
  const { open, setOpenSicuro, conferma } = useConferma(onConfirm);
  return (
    <AlertDialog open={open} onOpenChange={setOpenSicuro}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent className="rounded-3xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="confirm-cancel-btn">Annulla</AlertDialogCancel>
          <AlertDialogAction className="rounded-full bg-red-600 hover:bg-red-700" onClick={conferma} data-testid={testId || "confirm-ok-btn"}>
            Conferma
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Blur di una <select> dopo la scelta, ma SOLO su dispositivi touch.
 * Su iOS chiude la rotella del picker (altrimenti resta aperta); su desktop
 * (macOS in particolare) un blur() dentro onChange interrompe la chiusura del
 * menu nativo e la tendina si riapre al click successivo.
 */
// --- Consensi fotografici -------------------------------------------------
//
// Un consenso vale SOLO se è stato prestato in forma valida: `true` esplicito.
// `null` (mai chiesto, o record migrato dal vecchio `consenso_foto: true`, che
// era un default e non una scelta) e `false` valgono entrambi come "non si
// fotografa". La regola fallisce chiusa: nel dubbio, non si scatta.
export const CHIAVI_FOTO = ["foto_interne", "foto_parrocchia", "foto_web"];

export function consensoAttivo(doc, chiave) {
  return doc?.consensi?.[chiave]?.valore === true;
}

export function fotoVietate(doc) {
  return !CHIAVI_FOTO.some((k) => consensoAttivo(doc, k));
}

/** Badge da mostrare ovunque compaia un Animato, anche nelle liste che
 *  animatori e aiuti vedono in campo: è lì che serve, non solo in Segreteria. */
export function BadgeFoto({ animato, className = "" }) {
  if (!fotoVietate(animato)) return null;
  const daRiacquisire = animato?.consensi_da_riacquisire;
  return (
    <span
      className={`badge-neg ${className}`}
      title={daRiacquisire
        ? "Consenso mai raccolto in forma valida: da richiedere alla famiglia"
        : "La famiglia non ha prestato alcun consenso all'uso di foto o video"}
      data-testid="badge-no-foto"
    >
      No foto/video{daRiacquisire ? " (da verificare)" : ""}
    </span>
  );
}

export const blurSuTouch = (e) => {
  if (window.matchMedia("(pointer: coarse)").matches) e.target.blur();
};

export const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const fmtData = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export const GIORNI_SETTIMANA = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
export const TAGLIE = ["S", "M", "L", "XL", "XXL"];
export const COLORI_SQUADRA = ["#F59E0B", "#0EA5E9", "#10B981", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];
